import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_RoundedCorners = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🔲 CSS Rounded Corners</h1>

      <p className="tutorial-intro">
        CSS allows you to create <b>rounded corners</b> on elements using the{" "}
        <code>border-radius</code> property. Rounded corners improve aesthetics
        and make elements feel more modern and approachable.
      </p>

      {/* ✅ Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "0.5rem" }}>
          🔑 Key Points
        </h2>
        <ul
          style={{
            lineHeight: "1.8",
            fontSize: "1.05rem",
            paddingLeft: "25px",
          }}
        >
          <li>
            Use <code>border-radius</code> to control the roundness of corners.
          </li>
          <li>
            Values can be in <code>px</code>, <code>%</code>, <code>em</code>,
            etc.
          </li>
          <li>
            <code>50%</code> makes perfect circles (when width = height).
          </li>
          <li>
            Each corner can have its own radius (top-left, top-right, etc.).
          </li>
          <li>Great for buttons, cards, avatars, and modern layouts.</li>
        </ul>
      </div>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 1: Basic Rounded Corners</h2>
        <CodeCopyBlock
          code={`.box {
  border: 2px solid #2563eb;
  border-radius: 12px;
  padding: 20px;
}`}
          language="css"
        />

        <div
          style={{
            border: "2px solid #2563eb",
            borderRadius: "12px",
            padding: "20px",
            marginTop: "1rem",
            textAlign: "center",
          }}
        >
          Rounded Box (12px radius)
        </div>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 2: Circle & Ellipse</h2>
        <CodeCopyBlock
          code={`.circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
}
.ellipse {
  width: 180px;
  height: 100px;
  border-radius: 50%;
}`}
          language="css"
        />

        <div
          style={{
            display: "flex",
            gap: "20px",
            marginTop: "1rem",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              background: "#fca5a5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Circle
          </div>
          <div
            style={{
              width: "180px",
              height: "100px",
              borderRadius: "50%",
              background: "#86efac",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Ellipse
          </div>
        </div>
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 3: Pill Buttons</h2>
        <CodeCopyBlock
          code={`.pill-btn {
  background: #16a34a;
  color: white;
  padding: 10px 20px;
  border-radius: 9999px; /* fully rounded */
  border: none;
}`}
          language="css"
        />

        <button
          style={{
            background: "#16a34a",
            color: "white",
            padding: "10px 20px",
            borderRadius: "9999px",
            border: "none",
            marginTop: "1rem",
          }}
        >
          Pill Button
        </button>
      </div>

      {/* Example 4 */}
      <div className="tutorial-section">
        <h2 className="section-title">
          👨‍💻 Example 4: Card with Rounded Corners
        </h2>
        <CodeCopyBlock
          code={`.card {
  border-radius: 16px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  padding: 20px;
}`}
          language="css"
        />

        <div
          style={{
            borderRadius: "16px",
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            padding: "20px",
            marginTop: "1rem",
            background: "#fff",
            maxWidth: "250px",
          }}
        >
          <h3>Rounded Card</h3>
          <p>This card has smooth rounded corners.</p>
        </div>
      </div>

      {/* Example 5 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 5: Different Corner Radius</h2>
        <CodeCopyBlock
          code={`.box {
  border-radius: 30px 10px 50px 0;
}`}
          language="css"
        />

        <div
          style={{
            borderRadius: "30px 10px 50px 0",
            border: "2px solid #7c3aed",
            padding: "20px",
            marginTop: "1rem",
            textAlign: "center",
          }}
        >
          Mixed Corners
        </div>
      </div>

      {/* Example 6 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 6: Advanced Shape</h2>
        <CodeCopyBlock
          code={`.leaf {
  width: 120px;
  height: 200px;
  background: #34d399;
  border-radius: 100px 0;
}`}
          language="css"
        />

        <div
          style={{
            width: "120px",
            height: "200px",
            background: "#34d399",
            borderRadius: "100px 0",
            marginTop: "1rem",
          }}
        ></div>
      </div>

      {/* 🚀 Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Rounded corners are simple yet powerful in modern UI design. Try using{" "}
          <code>border-radius</code> with <b>shadows</b>, <b>gradients</b>, or{" "}
          <b>background images</b> to create polished interfaces. Experiment
          with <code>clip-path</code> and <code>shape-outside</code> for more
          advanced custom shapes.
        </p>
      </div>
    </div>
  );
};

export default CSS_RoundedCorners;
